// Platform seam for "Enable notifications" (docs/06 §2): inside the Android
// WebView the web `Notification.requestPermission()` is a silent no-op — the
// Android 13+ POST_NOTIFICATIONS prompt only exists natively, behind the
// MerkuryPush plugin. Composition rule as everywhere: probe isCapacitorNative()
// first, dynamically import the Capacitor adapter only when true.

import { isCapacitorNative } from '../shell/capacitor/platform.js';

// --- Platform detection for the Safari/iOS install path (JW9) --------------
//
// On iOS, Web Push (and therefore call notifications) exists ONLY for a web app
// added to the Home Screen — a plain Safari tab gets no push at all
// (web/jobs.md "Architecture reality" §4). We must detect that and steer the
// user to install instead of firing a doomed permission prompt. Elsewhere, a
// hard notification denial blocks the closed-app ring the same way. These are
// small pure helpers over the user agent + the display-mode media query so they
// stay unit-testable with mocked seams (no real globals in the logic).

/** Seams for the detectors, defaulted to the real browser globals but always
 *  guarded so they never throw off-DOM (SSR/tests). */
export interface PlatformSeams {
  userAgent?: string;
  maxTouchPoints?: number;
  /** `(display-mode: standalone)` match result. */
  standalone?: boolean;
  /** Legacy iOS Safari standalone flag (`navigator.standalone`). */
  navigatorStandalone?: boolean;
  /** `Notification.permission`, or undefined when the API is absent. */
  notificationPermission?: NotificationPermission | undefined;
  /** Capacitor native context — no web-install hint applies there. */
  native?: boolean;
}

function readUserAgent(seams: PlatformSeams): string {
  if (seams.userAgent !== undefined) return seams.userAgent;
  try {
    return globalThis.navigator?.userAgent ?? '';
  } catch {
    return '';
  }
}

/** iPhone/iPad/iPod, including iPadOS 13+ which reports as "Macintosh" but has
 *  a touch screen (maxTouchPoints > 1). */
export function isIosDevice(seams: PlatformSeams = {}): boolean {
  const ua = readUserAgent(seams);
  if (/iP(hone|ad|od)/.test(ua)) return true;
  const touch =
    seams.maxTouchPoints ??
    (() => {
      try {
        return globalThis.navigator?.maxTouchPoints ?? 0;
      } catch {
        return 0;
      }
    })();
  // iPadOS-as-desktop: Mac UA + a real touch screen ⇒ treat as iOS.
  return /Macintosh/.test(ua) && touch > 1;
}

/** Whether the app is running as an installed (standalone) PWA rather than in a
 *  browser tab — the iOS Home-Screen case, or a desktop installed window. */
export function isStandaloneDisplayMode(seams: PlatformSeams = {}): boolean {
  if (seams.standalone !== undefined || seams.navigatorStandalone !== undefined) {
    return Boolean(seams.standalone) || Boolean(seams.navigatorStandalone);
  }
  try {
    if (globalThis.matchMedia?.('(display-mode: standalone)').matches) return true;
    // iOS Safari predates display-mode and exposes navigator.standalone instead.
    return (globalThis.navigator as { standalone?: boolean } | undefined)?.standalone === true;
  } catch {
    return false;
  }
}

/** Why this context cannot receive call pushes, or null when it can (or when no
 *  hint should show). Drives both the onboarding step and the signed-in banner:
 *   - 'ios-install'         → iOS Safari tab: install to the Home Screen.
 *   - 'notifications-denied'→ permission is denied (desktop/macOS or an
 *                             installed iOS app whose notifications are off).
 *  Native (Capacitor) returns null — native handles its own notification path. */
export type InstallHintReason = 'ios-install' | 'notifications-denied';

function readNotificationPermission(seams: PlatformSeams): NotificationPermission | undefined {
  if ('notificationPermission' in seams) return seams.notificationPermission;
  try {
    return 'Notification' in globalThis ? Notification.permission : undefined;
  } catch {
    return undefined;
  }
}

export function callPushBlockReason(seams: PlatformSeams = {}): InstallHintReason | null {
  const native = seams.native ?? isCapacitorNative();
  if (native) return null;
  // An iOS Safari TAB can never receive push — installing is the only fix, and
  // it takes priority over a permission read (the tab can't even be granted).
  if (isIosDevice(seams) && !isStandaloneDisplayMode(seams)) return 'ios-install';
  // Everywhere else (and an installed iOS app): a hard denial blocks the ring.
  if (readNotificationPermission(seams) === 'denied') return 'notifications-denied';
  return null;
}

/** Check-only: whether notifications are already effectively enabled, WITHOUT
 *  ever prompting (native: plain plugin method over
 *  NotificationManagerCompat.areNotificationsEnabled; web: Notification.permission).
 *  Never throws; unknown/error reads as false so callers fail open to asking. */
export async function hasNotificationPermission(): Promise<boolean> {
  try {
    if (isCapacitorNative()) {
      const { nativePlugins } = await import('../shell/capacitor/plugins.js');
      const { granted } = await nativePlugins().push.getNotificationPermissionStatus();
      return granted;
    }
    return 'Notification' in globalThis && Notification.permission === 'granted';
  } catch {
    return false;
  }
}

/** Ask for notification permission on whichever platform we're on. Never throws. */
export async function requestNotificationPermission(): Promise<boolean> {
  try {
    if (isCapacitorNative()) {
      const { nativePlugins } = await import('../shell/capacitor/plugins.js');
      const { granted } = await nativePlugins().push.requestNotificationPermission();
      return granted;
    }
    if ('Notification' in globalThis) {
      return (await Notification.requestPermission()) === 'granted';
    }
  } catch {
    // Permission prompts can reject/throw on odd platforms — callers treat
    // "not granted" as skippable, never as an error.
  }
  return false;
}
